import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Eye, UserPlus, ChevronRight, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 활동 유형별 문구/아이콘/색상
export const ADMIRER_ACTION = {
  like: { text: "내 글에 좋아요를 눌렀습니다.", Icon: Heart, color: "text-rose-500", bg: "bg-rose-50" },
  visit: { text: "내 프로필을 방문했습니다.", Icon: Eye, color: "text-sky-500", bg: "bg-sky-50" },
  follow: { text: "나를 팔로우했습니다.", Icon: UserPlus, color: "text-burgundy-600", bg: "bg-burgundy-50" },
};

function ActorAvatar({ item, onClick }) {
  const src = item.profile_image
    ? (item.profile_image.startsWith("http") ? item.profile_image : `${API_URL}${item.profile_image}`)
    : null;
  return (
    <button
      onClick={onClick}
      className="w-9 h-9 rounded-full overflow-hidden shrink-0 border border-stone-100 hover:ring-2 hover:ring-burgundy-200 transition-all"
      data-testid={`admirer-avatar-${item.actor_id}`}
      title="프로필 보기"
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="w-full h-full flex items-center justify-center bg-gradient-to-br from-burgundy-400 to-rose-400 text-white text-sm font-bold">
          {(item.masked_name || "?").charAt(0)}
        </span>
      )}
    </button>
  );
}

/**
 * 좌측 사이드바 — "나에게 관심 있는 사람" 활동 패널.
 * 좋아요/프로필 방문/팔로우를 최신순으로 합쳐 최근 3건을 노출.
 */
export default function AdmirersCard({ inline = false }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, today_count: 0, unseen_count: 0 });

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/me/admirers?limit=3`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 사용자가 확인(클릭)하면 미확인 배지를 초기화. 그 전까지는 숫자가 계속 남아있음.
  const markSeenAndGo = async (path) => {
    try {
      await fetch(`${API_URL}/api/me/admirers/seen`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* silent */ }
    navigate(path);
  };

  if (!user) return null;

  const { items, total, today_count, unseen_count } = data;

  return (
    <div
      className={inline
        ? "mt-2 pt-3 border-t border-stone-100"
        : "bg-white rounded-2xl border border-stone-100 p-3.5 mb-3 shadow-[0_1px_3px_rgba(0,0,0,0.02)]"}
      data-testid="admirers-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Users className="w-4 h-4 text-burgundy-600" />
          <span className="text-[13px] font-bold text-stone-800">받은 관심</span>
          <span className="text-[12px] font-bold text-burgundy-600" data-testid="admirers-total">({unseen_count})</span>
        </div>
      </div>

      {today_count > 0 && (
        <p className="text-[11px] text-stone-500 mb-2.5 pl-0.5" data-testid="admirers-today">
          오늘 <span className="font-bold text-burgundy-600">{today_count}명</span>
        </p>
      )}

      {items.length === 0 ? (
        <p className="text-[12px] text-stone-400 py-3 text-center" data-testid="admirers-empty">
          아직 관심을 보낸 분이 없어요 🌿
        </p>
      ) : (
        <ul className="space-y-2" data-testid="admirers-list">
          {items.slice(0, 1).map((it, idx) => {
            const action = ADMIRER_ACTION[it.type] || ADMIRER_ACTION.visit;
            const ActIcon = action.Icon;
            return (
              <li key={`${it.actor_id}-${it.type}-${idx}`} className="flex items-center gap-2.5">
                <ActorAvatar item={it} onClick={() => markSeenAndGo(`/timeline/${it.actor_id}`)} />
                <button
                  onClick={() => markSeenAndGo(`/timeline/${it.actor_id}`)}
                  className="flex-1 min-w-0 text-left group"
                  data-testid={`admirer-row-${it.actor_id}`}
                >
                  <p className="text-[12.5px] text-stone-700 leading-snug truncate">
                    <span className="font-semibold text-stone-900 group-hover:text-burgundy-700">{it.masked_name}</span>
                    <span className="text-stone-500"> 님이 {action.text}</span>
                  </p>
                </button>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${action.bg}`}>
                  <ActIcon className={`w-3.5 h-3.5 ${action.color}`} />
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <button
        onClick={() => markSeenAndGo("/admirers")}
        className="mt-3 w-full py-2 rounded-xl bg-stone-50 hover:bg-burgundy-50 text-burgundy-700 text-[12.5px] font-semibold flex items-center justify-center gap-1 transition-colors"
        data-testid="admirers-view-all"
      >
        전체 보기 <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
